import { type Address, erc20Abi as ERC20_ABI } from 'viem'
import { mainnet } from 'viem/chains'
import { indexBy } from '../utils'
import { MATCH_ALL_CONDITION, tokenAddresses } from './consts'
import type {
  DynamicRoute,
  DynamicRouteConfig,
  DynamicRoutePath,
  DynamicRoutingConfig,
  IndexedDynamicRoutingConfig,
  RuleDefinition,
} from './types'

export const createPath = (config: DynamicRoutePath): DynamicRoutePath => {
  return config
}

export const createRoute = (config: DynamicRouteConfig): DynamicRoute => {
  const getPathById = (pathId: string): DynamicRoutePath | null => {
    const path = config.paths[pathId]
    if (!path) {
      return null
    }
    return path
  }

  return {
    id: config.id,
    config,
    getPathById,
  }
}

const resolveBadge = (rule: RuleDefinition) => {
  if ('badge' in rule.use) {
    return rule.use.badge
  }
  return undefined
}

const resolveForceBridge = (rule: RuleDefinition) => {
  if ('bridgeOverride' in rule.use) {
    return rule.use.bridgeOverride
  }
  return undefined
}

export const initDynamicRouting = (
  dynamicRoutes: DynamicRoute[],
  dynamicRoutingConfig: DynamicRoutingConfig,
) => {
  const indexedRoutes = indexBy(dynamicRoutes, (route) => route.id)
  const indexedDynamicRoutingConfig = dynamicRoutingConfig.reduce(
    (acc, routeConfig) => {
      const route = indexedRoutes[routeConfig.routeId]
      if (!route) {
        return acc
      }
      const { rules } = routeConfig

      // index rules by when.chainId and when.tokenAddress
      const indexedRules = indexBy([...rules], (rule) => {
        if (rule.when === MATCH_ALL_CONDITION) {
          return MATCH_ALL_CONDITION
        }
        return `${rule.when.chainId}-${rule.when.tokenAddress}`.toLowerCase()
      })

      const resolveRule = (rule: RuleDefinition) => {
        const path = route.getPathById(rule.use.path)
        if (!path) {
          return null
        }
        const badge = resolveBadge(rule)
        const bridgeOverride = resolveForceBridge(rule)
        return {
          path,
          badge,
          bridgeOverride,
        }
      }

      const findPath = ({
        chainId,
        tokenAddress,
      }: {
        chainId: string
        tokenAddress: string
      }) => {
        // try to match rule definitively (by chainId and tokenAddress) first
        const specificRule =
          indexedRules[`${chainId}-${tokenAddress}`.toLowerCase()]
        if (specificRule) {
          return resolveRule(specificRule)
        }

        // if no specific rule found, fallback to wildcard rule
        // FIRST, check if there is a rule with chainId = '*' and tokenAddress matching
        const anyChainSpecificTokenAddress =
          indexedRules[`${MATCH_ALL_CONDITION}-${tokenAddress}`.toLowerCase()]
        if (anyChainSpecificTokenAddress) {
          return resolveRule(anyChainSpecificTokenAddress)
        }

        // SECOND, check if there is a rule with tokenAddress = '*' and chainId matching
        const anyTokenAddressSpecificChain =
          indexedRules[`${chainId}-${MATCH_ALL_CONDITION}`.toLowerCase()]
        if (anyTokenAddressSpecificChain) {
          return resolveRule(anyTokenAddressSpecificChain)
        }

        // Third - check general purpose wildcard rule
        const wildcardRule = indexedRules[MATCH_ALL_CONDITION]
        if (wildcardRule) {
          return resolveRule(wildcardRule)
        }

        // This should never happen
        // As there should always be a fallback defined
        return null
      }

      acc[routeConfig.routeId] = {
        route,
        rules,
        findPath,
      }
      return acc
    },
    {} as IndexedDynamicRoutingConfig,
  )

  return indexedDynamicRoutingConfig
}

export const MAX_APPROVAL =
  '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
/**
 * Returns args for the approve function call.
 * @param tokenAddress - The address of the token to approve
 * @param spenderAddress - The address of the spender to approve
 * @param amount - The amount of tokens to approve
 */
export const getApprovalCalldata = ({
  tokenAddress,
  spenderAddress,
  amount,
}: {
  tokenAddress: Address
  spenderAddress: Address
  amount: string
}) => {
  return {
    contractAbi: ERC20_ABI,
    contractAddress: tokenAddress,
    functionName: 'approve' as const,
    functionArgs: [spenderAddress, amount],
  }
}

/**
 * Returns args for the approve function call to reset the approval to 0.
 * @param tokenAddress - The address of the token to reset the approval for
 * @param spenderAddress - The address of the spender to reset the approval for
 */
export const getResetApprovalCalldata = ({
  tokenAddress,
  spenderAddress,
}: {
  tokenAddress: Address
  spenderAddress: Address
}) => {
  return getApprovalCalldata({
    tokenAddress,
    spenderAddress,
    amount: '0',
  })
}

/**
 * These tokens have a brain dead implementation of the approve function.
 * They require the approval to be reset to 0 before the approval can be set to a new value.
 */
export const RESET_APPROVAL_TOKENS = [tokenAddresses.LDO[mainnet.id]].map(
  (address) => address.toLowerCase(),
)

/**
 * Checks if a token requires approval reset before approval can be set to a new value due to non-standard implementation of the approve function.
 *
 * @param tokenAddress - The address of the token to check if it requires approval reset before approval can be set to a new value.
 * @returns true if the token requires approval reset before approval can be set to a new value, false otherwise.
 */
export const shouldResetApproval = (tokenAddress: Address) => {
  return RESET_APPROVAL_TOKENS.includes(tokenAddress.toLowerCase())
}
